<template>
  <div class="app-container">
    <div class="filter-container">
      <el-input v-model="query.keyword" :placeholder="$t('table.keyword')" style="width: 200px;" class="filter-item" @keyup.enter.native="handleFilter" />
      <el-select v-model="query.role" :placeholder="$t('table.role')" clearable style="width: 90px" class="filter-item" @change="handleFilter">
        <el-option v-for="item in roles" :key="item" :label="item | uppercaseFirst" :value="item" />
      </el-select>
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="handleFilter">
        {{ $t('table.search') }}
      </el-button>
      <el-button v-permission="['manage user']" class="filter-item" style="margin-left: 10px;" type="success" icon="el-icon-plus" @click="handleCreate">
        Dodaj użytkownika
      </el-button>
    </div>

    <div v-if="selectedUsers.length > 0" class="bulk-actions" data-testid="users-bulk-bar">
      <el-alert
        :title="'Zaznaczono ' + selectedUsers.length + ' użytkowników (także z innych stron listy)'"
        type="info"
        :closable="false"
        show-icon
        style="display: inline-flex; width: auto; margin-right: 10px;"
      />
      <el-button
        v-permission="['manage user']"
        type="primary"
        size="small"
        icon="el-icon-video-camera"
        data-testid="users-bulk-reels-open"
        @click="openBulkReels"
      >
        Rolki / rabat dla zaznaczonych ({{ selectedUsers.length }})
      </el-button>
      <el-button size="small" @click="clearSelection">Odznacz wszystkie</el-button>
    </div>

    <el-table ref="userTable" v-loading="loading" :data="list" border fit highlight-current-row style="width: 100%"
              :default-sort="{ prop: 'created_at', order: 'descending' }"
              row-key="id"
              @sort-change="handleSortChange"
              @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="45" :reserve-selection="true" :selectable="isSelectable" />
      <el-table-column align="center" label="ID" width="80">
        <template slot-scope="scope">
          <span>{{ scope.row.index }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Nazwa">
        <template slot-scope="scope">
          <div class="user-info-cell">
            <img v-if="scope.row.avatar_url" :src="scope.row.avatar_url" class="user-avatar-mini" alt="Avatar" />
            <div class="user-details">
              <span class="user-name">{{ scope.row.name }}</span>
              <div v-if="scope.row.video_avatar_url" class="video-indicator">
                <i class="el-icon-video-camera" title="Ma video avatar"></i>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Email">
        <template slot-scope="scope">
          <span>{{ scope.row.email }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Rolki" width="80">
        <template slot-scope="scope">
          <span data-testid="users-ai-credits">{{ scope.row.ai_credits || 0 }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Rola" width="150">
        <template slot-scope="scope">
          <div>
            <span>{{ scope.row.roles.join(', ') }}</span>
            <div v-if="needsCompanyFix(scope.row)" class="company-warning">
              <el-tag type="warning" size="mini">
                <i class="el-icon-warning"></i> Brak firmy
              </el-tag>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Created_at" width="160" prop="created_at" sortable="custom">
        <template slot-scope="scope">
          <span>{{ scope.row.created_at }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Akcje" width="350">
        <template slot-scope="scope" >
          <div v-if="!scope.row.deleted_at">
            <el-button v-permission="['manage user']" type="primary" size="small" icon="el-icon-edit" @click="edit(scope.row.id)">
              Edit
            </el-button>
            <el-button v-if="!scope.row.roles.includes('admin')" v-permission="['manage permission']" type="warning" size="small" icon="el-icon-edit" @click="handleEditPermissions(scope.row.id);">
              Uprawnienia
            </el-button>
            <el-button v-if="needsCompanyFix(scope.row)" v-permission="['manage user']" type="success" size="small" icon="el-icon-office-building" @click="handleFixCompany(scope.row.id, scope.row.name);">
              Utwórz firmę
            </el-button>
            <el-button v-if="!scope.row.roles.includes('admin')" v-permission="['manage user']" type="danger" size="small" icon="el-icon-delete" @click="handleDelete(scope.row.id, scope.row.email);">
              Usuń
            </el-button>
          </div>
          <div v-else>
            W trakcie usuwania...
            <el-button v-if="!scope.row.roles.includes('admin')" v-permission="['manage user']" type="danger" size="small" icon="el-icon-delete" @click="handleDelete(scope.row.id, scope.row.email, true)">
              Usuń
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <pagination v-show="total>0" :total="total" :page.sync="query.page" :limit.sync="query.limit" @pagination="getList" />

    <el-dialog :visible.sync="dialogPermissionVisible" :title="'Edytuj Uprawnienia - ' + currentUser.name">
      <div v-if="currentUser.name" v-loading="dialogPermissionLoading" class="form-container">
        <div class="permissions-container">
          <div class="block">
            <el-form :model="currentUser" label-width="80px" label-position="top">
              <el-form-item label="Permissions">
                <el-tree ref="otherPermissions" :data="normalizedOtherPermissions" :default-checked-keys="permissionKeys(userOtherPermissions)" :props="permissionProps" show-checkbox node-key="id" class="permission-tree" />
              </el-form-item>
            </el-form>
          </div>
          <div class="clear-left" />
        </div>
        <div style="text-align:right;">
          <el-button type="danger" @click="dialogPermissionVisible=false">
            Anuluj
          </el-button>
          <el-button type="primary" @click="confirmPermission">
            Zapisz
          </el-button>
        </div>
      </div>
    </el-dialog>
    <el-dialog :visible.sync="dialogDeleteVisible" :title="'delete ' + user_email">
      <div v-if="user_email" v-loading="dialogDeleteLoading" class="form-container">
        <div class="permissions-container" v-if="!user_delayed_deletion">
          <div class="block">
            <el-checkbox
              v-model="delayedDeletion"
            >  delete after six months
            </el-checkbox>
          </div>
          <div class="clear-left" />
        </div>
        <div style="text-align:right;">
          <el-button type="danger" @click="dialogDeleteVisible=false">
            Anuluj
          </el-button>
          <el-button type="primary" @click="confirmDeletion">
            Zapisz
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- Create User Dialog -->
    <el-dialog :visible.sync="dialogFormVisible" :title="'Dodaj nowego użytkownika'">
      <div v-loading="userCreating" class="form-container">
        <el-form ref="userForm" :model="newUser" :rules="rules" label-width="120px" label-position="left">
          <el-form-item label="Nazwa/Imię" prop="name">
            <el-input v-model="newUser.name" placeholder="Wprowadź nazwę firmy lub imię" />
          </el-form-item>
          <el-form-item label="Email" prop="email">
            <el-input v-model="newUser.email" placeholder="Wprowadź adres email" />
          </el-form-item>
          <el-form-item label="Hasło" prop="password">
            <div class="password-input-group">
              <el-input v-model="newUser.password" type="password" placeholder="Wprowadź hasło" />
              <el-button size="small" type="primary" @click="generatePassword" class="generate-btn">
                Generuj hasło
              </el-button>
            </div>
            <div v-if="generatedPassword" class="generated-password-display">
              <p><strong>Wygenerowane hasło:</strong> {{ generatedPassword }}</p>
              <p class="password-note">Zapisz to hasło - nie będzie ponownie wyświetlone!</p>
            </div>
          </el-form-item>
          <el-form-item label="Potwierdź hasło" prop="confirmPassword">
            <el-input v-model="newUser.confirmPassword" type="password" placeholder="Potwierdź hasło" />
          </el-form-item>
          
          <!-- Password Strength Indicator -->
          <div v-if="newUser.password" class="password-strength">
            <p class="strength-title">Siła hasła:</p>
            <div class="strength-indicators">
              <p :class="{ 'text-green-500': hasMinLength, 'text-red-500': !hasMinLength }">
                <span v-if="hasMinLength">✅</span><span v-else>❌</span> Więcej niż 7 liter
              </p>
              <p :class="{ 'text-green-500': hasUppercase, 'text-red-500': !hasUppercase }">
                <span v-if="hasUppercase">✅</span><span v-else>❌</span> Ma min jedną wielką literę
              </p>
              <p :class="{ 'text-green-500': hasLowercase, 'text-red-500': !hasLowercase }">
                <span v-if="hasLowercase">✅</span><span v-else>❌</span> Ma min jedną małą literę
              </p>
              <p :class="{ 'text-green-500': hasNumber, 'text-red-500': !hasNumber }">
                <span v-if="hasNumber">✅</span><span v-else>❌</span> Ma min jedną cyfrę
              </p>
              <p :class="{ 'text-green-500': hasSpecialChar, 'text-red-500': !hasSpecialChar }">
                <span v-if="hasSpecialChar">✅</span><span v-else>❌</span> Ma min jeden znak specjalny
              </p>
            </div>
          </div>
          <el-form-item label="Telefon">
            <el-input v-model="newUser.phone" placeholder="Numer telefonu" />
          </el-form-item>
          <el-form-item label="Miasto">
            <el-input v-model="newUser.city" placeholder="Miasto" />
          </el-form-item>
          <el-form-item label="Ulica">
            <el-input v-model="newUser.street" placeholder="Ulica i numer" />
          </el-form-item>
          <el-form-item label="Kod pocztowy">
            <el-input v-model="newUser.zip_code" placeholder="Kod pocztowy" />
          </el-form-item>
          <el-form-item label="Kraj">
            <el-input v-model="newUser.country" placeholder="Kraj" />
          </el-form-item>
          <el-form-item label="NIP">
            <el-input v-model="newUser.nip" placeholder="NIP (opcjonalnie)" />
          </el-form-item>
          <el-form-item label="Typ konta" prop="accountType">
            <el-select v-model="newUser.accountType" placeholder="Wybierz typ konta">
              <el-option v-for="item in accountTypes" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          
          <!-- Avatar Section -->
          <el-form-item label="Avatar">
            <div class="avatar-upload-section">
              <el-upload
                class="avatar-uploader"
                :action="uploadUrl"
                :show-file-list="false"
                :on-success="handleAvatarSuccess"
                :before-upload="beforeAvatarUpload"
                :headers="uploadHeaders"
                :data="{ avatar_type: 'photo' }"
                :with-credentials="true"
                name="avatar"
              >
                <img v-if="newUser.avatarUrl" :src="newUser.avatarUrl" class="avatar-preview">
                <i v-else class="el-icon-plus avatar-uploader-icon"></i>
              </el-upload>
              <div class="upload-tips">
                <p>Kliknij aby przesłać avatar</p>
                <p>Obsługiwane formaty: JPG, PNG do 2MB</p>
              </div>
              <el-button v-if="newUser.avatarUrl" size="small" type="danger" @click="removeAvatar">
                Usuń avatar
              </el-button>
            </div>
          </el-form-item>
          
          <!-- Video Avatar Section -->
          <el-form-item label="Video Avatar">
            <div class="video-avatar-section">
              <el-upload
                class="video-uploader"
                :action="videoUploadUrl"
                :show-file-list="false"
                :on-success="handleVideoAvatarSuccess"
                :before-upload="beforeVideoUpload"
                :headers="uploadHeaders"
                :data="{ avatar_type: 'video_url' }"
                :with-credentials="true"
                name="video_avatar"
              >
                <video v-if="newUser.videoAvatarUrl" :src="newUser.videoAvatarUrl" class="video-preview" controls></video>
                <i v-else class="el-icon-video-camera video-uploader-icon"></i>
              </el-upload>
              <div class="upload-tips">
                <p>Kliknij aby przesłać video avatar</p>
                <p>Obsługiwane formaty: MP4, WEBM do 10MB</p>
              </div>
              <el-button v-if="newUser.videoAvatarUrl" size="small" type="danger" @click="removeVideoAvatar">
                Usuń video avatar
              </el-button>
            </div>
          </el-form-item>
        </el-form>
        <div style="text-align:right;">
          <el-button type="danger" @click="dialogFormVisible=false">
            Anuluj
          </el-button>
          <el-button type="primary" :loading="userCreating" @click="createUser">
            Utwórz użytkownika
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- Rolki / rabat dla zaznaczonych -->
    <el-dialog
      :visible.sync="bulkReelsVisible"
      :title="'Rolki / rabat dla zaznaczonych (' + selectedUsers.length + ')'"
      width="600px"
    >
      <el-form label-width="120px" size="small" @submit.native.prevent>
        <el-form-item label="Dodaj rolki">
          <el-checkbox v-model="bulkForm.withCredits" data-testid="bulk-reels-with-credits">tak</el-checkbox>
          <el-input-number
            v-model="bulkForm.credits"
            :min="1"
            :max="maxAmount"
            :disabled="!bulkForm.withCredits"
            style="margin-left: 12px;"
            data-testid="bulk-reels-credits"
          />
        </el-form-item>
        <el-form-item label="Dodaj rabat">
          <el-checkbox v-model="bulkForm.withDiscount" data-testid="bulk-reels-with-discount">tak</el-checkbox>
        </el-form-item>
        <discount-fields v-if="bulkForm.withDiscount" :form="bulkForm.discount" />
        <el-form-item label="Notatka">
          <el-input
            v-model="bulkForm.note"
            :maxlength="noteMaxLength"
            placeholder="opcjonalnie, np. pierwsze 12 osób z grupy"
            show-word-limit
            data-testid="bulk-reels-note"
          />
        </el-form-item>
        <el-form-item label="">
          <el-checkbox v-model="bulkForm.notify" data-testid="bulk-reels-notify">Powiadom użytkowników</el-checkbox>
          <div class="bulk-notify-hint">{{ notifyHint }} Rolki i rabat razem = jedno powiadomienie na osobę.</div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="bulkReelsVisible = false">Anuluj</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="bulkSaving"
          data-testid="bulk-reels-submit"
          @click="submitBulkReels"
        >
          Zapisz dla {{ selectedUsers.length }}
        </el-button>
      </div>
    </el-dialog>

    <!-- Fix Company Dialog -->
    <el-dialog :visible.sync="dialogFixCompanyVisible" :title="'Utwórz firmę dla: ' + currentFixUserName" width="400px">
      <div v-loading="fixCompanyLoading" class="form-container">
        <el-form label-width="120px" label-position="left">
          <el-form-item label="Typ firmy">
            <el-select v-model="selectedCompanyType" placeholder="Wybierz typ firmy" style="width: 100%">
              <el-option
                v-for="item in companyTypes"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <div class="company-type-info">
              <p><strong>Zostanie utworzona firma:</strong></p>
              <p>• <strong>Nazwa:</strong> {{ currentFixUserName }}</p>
              <p>• <strong>Typ:</strong> {{ getCompanyTypeLabel(selectedCompanyType) }}</p>
              <p class="info-note">Nazwa i typ firmy będą mogły być zmienione później w ustawieniach użytkownika.</p>
            </div>
          </el-form-item>
        </el-form>
        <div style="text-align:right;">
          <el-button type="danger" @click="dialogFixCompanyVisible=false">
            Anuluj
          </el-button>
          <el-button type="primary" :loading="fixCompanyLoading" @click="confirmFixCompany">
            Utwórz firmę
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'; // Secondary package based on el-pagination
import UserResource from '@/api/user';
import Resource from '@/api/resource';
import waves from '@/directive/waves'; // Waves directive
import permission from '@/directive/permission'; // Permission directive
import checkPermission from '@/utils/permission'; // Permission checking
import { csrf } from '@/api/auth'; // CSRF token handling
import { bulkReels } from '@/api/reels';
import { MAX_AMOUNT, MAX_DISCOUNT_PERCENT, NOTE_MAX_LENGTH, NOTIFY_HINT, emptyDiscountForm, discountPayload, noteOrNull, reelsCountLabel } from '@/utils/reels';
import DiscountFields from '@/views/reels/components/DiscountFields';

const BULK_REELS_MAX_USERS = 200;

const userResource = new UserResource();
const permissionResource = new Resource('permissions');

export default {
  name: 'UserList',
  components: { Pagination, DiscountFields },
  directives: { waves, permission },
  data() {
    var validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.newUser.password) {
        callback(new Error('Hasła muszą być identyczne!'));
      } else {
        callback();
      }
    };
    return {
      user_id: '',
      user_email: '',
      user_delayed_deletion: false,
      delayedDeletion: false,
      list: null,
      total: 0,
      loading: true,
      downloading: false,
      userCreating: false,
      uploadUrl: process.env.VUE_APP_BASE_API + '/admin/upload/avatar',
      videoUploadUrl: process.env.VUE_APP_BASE_API + '/admin/upload/video-avatar',
      generatedPassword: '',
      query: {
        page: 1,
        limit: 15,
        keyword: '',
        role: '',
        sort: '-created_at',
      },
      roles: ['admin', 'mod', 'user', 'agent', 'company'],
      nonAdminRoles: ['mod', 'user', 'agent', 'company'],
      accountTypes: [
        { label: 'Agencja nieruchomości', value: 'agency' },
        { label: 'Deweloper', value: 'developer' },
        { label: 'Osoba prywatna', value: 'user' },
        { label: 'Hotel/Pensjonat', value: 'hotel' },
        { label: 'Agent (pracownik)', value: 'agent' },
        { label: 'Moderator', value: 'mod' }
      ],
      newUser: {},
      dialogFormVisible: false,
      dialogPermissionVisible: false,
      dialogPermissionLoading: false,
      dialogDeleteLoading: false,
      dialogDeleteVisible: false,
      dialogFixCompanyVisible: false,
      fixCompanyLoading: false,
      currentUserId: 0,
      currentFixUserId: 0,
      currentFixUserName: '',
      selectedCompanyType: 'agency',
      companyTypes: [
        { value: 'agency', label: 'Agencja nieruchomości' },
        { value: 'developer', label: 'Deweloper' }
      ],
      currentUser: {
        name: '',
        permissions: [],
        rolePermissions: [],
      },
      rules: {
        accountType: [{ required: true, message: 'Typ konta jest wymagany', trigger: 'change' }],
        name: [{ required: true, message: 'Nazwa jest wymagana', trigger: 'blur' }],
        email: [
          { required: true, message: 'Email jest wymagany', trigger: 'blur' },
          { type: 'email', message: 'Wprowadź prawidłowy adres email', trigger: ['blur', 'change'] },
        ],
        password: [{ required: true, message: 'Hasło jest wymagane', trigger: 'blur' },
                   { min: 8, message: 'Hasło musi mieć co najmniej 8 znaków', trigger: 'blur' },
                   { validator: this.validatePasswordStrength, trigger: 'blur' }],
        confirmPassword: [{ validator: validateConfirmPassword, trigger: 'blur' }],
      },
      permissionProps: {
        children: 'children',
        label: 'name',
        disabled: 'disabled',
      },
      permissions: [],
      menuPermissions: [],
      otherPermissions: [],
      selectedUsers: [],
      bulkReelsVisible: false,
      bulkSaving: false,
      bulkForm: this.emptyBulkForm(),
      maxAmount: MAX_AMOUNT,
      noteMaxLength: NOTE_MAX_LENGTH,
      notifyHint: NOTIFY_HINT,
    };
  },
  computed: {
    normalizedMenuPermissions() {
      let tmp = [];
      this.currentUser.permissions.role.forEach(permission => {
        tmp.push({
          id: permission.id,
          name: permission.name,
          disabled: true,
        });
      });
      const rolePermissions = {
        id: -1, // Just a faked ID
        name: 'Inherited from role',
        disabled: true,
        children: this.classifyPermissions(tmp).menu,
      };

      tmp = this.menuPermissions.filter(permission => !this.currentUser.permissions.role.find(p => p.id === permission.id));
      const userPermissions = {
        id: 0, // Faked ID
        name: 'Extra menus',
        children: tmp,
        disabled: tmp.length === 0,
      };

      return [rolePermissions, userPermissions];
    },
    normalizedOtherPermissions() {
      let tmp = [];
      this.currentUser.permissions.role.forEach(permission => {
        tmp.push({
          id: permission.id,
          name: permission.name,
          disabled: true,
        });
      });
      const rolePermissions = {
        id: -1,
        name: 'Inherited from role',
        disabled: true,
        children: this.classifyPermissions(tmp).other,
      };

      tmp = this.otherPermissions.filter(permission => !this.currentUser.permissions.role.find(p => p.id === permission.id));
      const userPermissions = {
        id: 0,
        name: 'Extra permissions',
        children: tmp,
        disabled: tmp.length === 0,
      };

      return [rolePermissions, userPermissions];
    },
    userMenuPermissions() {
      return this.classifyPermissions(this.userPermissions).menu;
    },
    userOtherPermissions() {
      return this.classifyPermissions(this.userPermissions).other;
    },
    userPermissions() {
      return this.currentUser.permissions.role.concat(this.currentUser.permissions.user);
    },
  },
  created() {
    this.resetNewUser();
    this.getList();
    if (checkPermission(['manage permission'])) {
      this.getPermissions();
    }
  },
  methods: {
    edit(id) {
      this.$router.push({ path: '/administrator/users/edit?id=' + id });
    },
    isSelectable(row) {
      return !row.deleted_at;
    },
    handleSelectionChange(selection) {
      this.selectedUsers = selection;
    },
    clearSelection() {
      if (this.$refs.userTable) {
        this.$refs.userTable.clearSelection();
      }
      this.selectedUsers = [];
    },
    emptyBulkForm() {
      return {
        withCredits: true,
        credits: 1,
        withDiscount: false,
        discount: emptyDiscountForm(),
        note: '',
        notify: true,
      };
    },
    openBulkReels() {
      this.bulkForm = this.emptyBulkForm();
      this.bulkReelsVisible = true;
    },
    async submitBulkReels() {
      const form = this.bulkForm;
      const count = this.selectedUsers.length;
      if (count === 0) {
        return;
      }
      if (count > BULK_REELS_MAX_USERS) {
        this.$message.warning('Jednorazowo można zmienić maksymalnie ' + BULK_REELS_MAX_USERS + ' użytkowników.');
        return;
      }
      if (!form.withCredits && !form.withDiscount) {
        this.$message.warning('Zaznacz "Dodaj rolki" albo "Dodaj rabat".');
        return;
      }
      if (form.withCredits && (!Number.isInteger(form.credits) || form.credits < 1 || form.credits > MAX_AMOUNT)) {
        this.$message.warning('Podaj liczbę rolek od 1 do ' + MAX_AMOUNT + '.');
        return;
      }
      if (form.withDiscount) {
        const percent = form.discount.percent;
        if (!Number.isInteger(percent) || percent < 1 || percent > MAX_DISCOUNT_PERCENT) {
          this.$message.warning('Rabat musi wynosić od 1 do ' + MAX_DISCOUNT_PERCENT + '%.');
          return;
        }
        if (!form.discount.unlimited && !Number.isInteger(form.discount.maxUses)) {
          this.$message.warning('Podaj liczbę użyć rabatu albo zaznacz "bez limitu".');
          return;
        }
      }

      const parts = [];
      if (form.withCredits) {
        parts.push(reelsCountLabel(form.credits));
      }
      if (form.withDiscount) {
        parts.push('rabat ' + form.discount.percent + '%');
      }
      try {
        await this.$confirm(
          'Dodać ' + parts.join(' i ') + ' dla ' + count + ' zaznaczonych użytkowników?',
          'Potwierdzenie',
          {
            confirmButtonText: 'Zapisz',
            cancelButtonText: 'Anuluj',
            type: 'warning',
          }
        );
      } catch (cancel) {
        return;
      }

      const payload = {
        user_ids: this.selectedUsers.map(user => user.id),
        note: noteOrNull(form.note),
        notify: form.notify,
      };
      if (form.withCredits) {
        payload.credits = form.credits;
      }
      if (form.withDiscount) {
        payload.discount = discountPayload(form.discount);
      }

      this.bulkSaving = true;
      try {
        const { updated } = await bulkReels(payload);
        this.$message.success(
          'Zapisano dla ' + updated + ' użytkowników. ' +
          (payload.notify ? 'Użytkownicy zostaną powiadomieni.' : 'Bez powiadomienia użytkowników.')
        );
        this.bulkReelsVisible = false;
        this.clearSelection();
        this.getList();
      } catch (error) {
        // Komunikat błędu pokazuje już interceptor axios (src/utils/request.js).
      } finally {
        this.bulkSaving = false;
      }
    },
    checkPermission,
    async getPermissions() {
      const { data } = await permissionResource.list({});
      const { all, menu, other } = this.classifyPermissions(data);
      this.permissions = all;
      this.menuPermissions = menu;
      this.otherPermissions = other;
    },

    async getList() {
      const { limit, page } = this.query;
      this.loading = true;
      const { data, total } = await userResource.list(this.query);
      this.list = data;
      this.list.forEach((element, index) => {
        element['index'] = (page - 1) * limit + index + 1;
      });
      this.total = total;
      this.loading = false;
      // Element UI z sortable="custom" nie podswietla strzalki przy default-sort.
      // Po pierwszym fetchu wymuszamy wizualne wyroznienie, ale tylko raz —
      // .sort() emituje sort-change -> handleSortChange -> getList (infinite loop bez flagi).
      if (!this._initialSortApplied) {
        this._initialSortApplied = true;
        this.$nextTick(() => {
          if (this.$refs.userTable && typeof this.$refs.userTable.sort === 'function') {
            const dir = this.query.sort && this.query.sort.startsWith('-') ? 'descending' : 'ascending';
            const col = (this.query.sort || '-created_at').replace(/^-/, '');
            this.$refs.userTable.sort(col, dir);
          }
        });
      }
    },
    handleFilter() {
      this.query.page = 1;
      this.getList();
    },
    handleSortChange({ prop, order }) {
      const next = (!order || !prop)
        ? '-created_at'
        : (order === 'descending' ? '-' : '') + prop;
      if (next === this.query.sort) {
        return; // programowy .sort() po pierwszym fetchu — nie refetchuj
      }
      this.query.sort = next;
      this.query.page = 1;
      this.getList();
    },
    handleCreate() {
      this.resetNewUser();
      this.dialogFormVisible = true;
      this.$nextTick(() => {
        this.$refs['userForm'].clearValidate();
      });
    },
    async handleDelete(id, email, delayed_deletion = false) {
      this.dialogDeleteVisible = true;
      this.dialogDeleteLoading = true;
      this.user_email = email;
      this.user_delayed_deletion = delayed_deletion;
      this.user_id = id;
      this.dialogDeleteLoading = false;
    },
    confirmDeletion(){
      if(this.delayedDeletion){
        let user = {
          'delayedDeletion': true
        }
        userResource.update(this.user_id, user).then(response => {
          this.$message({
            type: 'success',
            message: 'ten użytkownik zostanie usunięty po 6 miesiącach',
          });
          this.handleFilter();
        }).catch(error => {
          console.log(error);
        });
      }else{
        userResource.destroy(this.user_id).then(response => {
          this.$message({
            type: 'success',
            message: 'Poprawnie usunięto użytkownika',
          });
          this.handleFilter();
        }).catch(error => {
          console.log(error);
        });
      }
      this.delayedDeletion = false;
      this.dialogDeleteVisible = false;

    },
    async handleEditPermissions(id) {
      this.currentUserId = id;
      this.dialogPermissionLoading = true;
      this.dialogPermissionVisible = true;
      const found = this.list.find(user => user.id === id);
      const { data } = await userResource.permissions(id);
      this.currentUser = {
        id: found.id,
        name: found.name,
        permissions: data,
      };
      this.dialogPermissionLoading = false;
      this.$nextTick(() => {
        this.$refs.menuPermissions.setCheckedKeys(this.permissionKeys(this.userMenuPermissions));
        this.$refs.otherPermissions.setCheckedKeys(this.permissionKeys(this.userOtherPermissions));
      });
    },
    resetNewUser() {
      this.newUser = {
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        accountType: 'user',
        phone: '',
        city: '',
        street: '',
        zip_code: '',
        country: '',
        nip: '',
        avatarUrl: '',
        videoAvatarUrl: '',
        avatar: null,
        video_avatar: null,
      };
      this.generatedPassword = '';
    },
    handleDownload() {
      this.downloading = true;
      import('@/vendor/Export2Excel').then(excel => {
        const tHeader = ['id', 'user_id', 'name', 'email', 'role'];
        const filterVal = ['index', 'id', 'name', 'email', 'role'];
        const data = this.formatJson(filterVal, this.list);
        excel.export_json_to_excel({
          header: tHeader,
          data,
          filename: 'user-list',
        });
        this.downloading = false;
      });
    },
    formatJson(filterVal, jsonData) {
      return jsonData.map(v => filterVal.map(j => v[j]));
    },
    permissionKeys(permissions) {
      return permissions.map(permssion => permssion.id);
    },
    classifyPermissions(permissions) {
      const all = []; const menu = []; const other = [];
      permissions.forEach(permission => {
        const permissionName = permission.name;
        all.push(permission);
        if (permissionName.startsWith('view menu')) {
          menu.push(this.normalizeMenuPermission(permission));
        } else {
          other.push(this.normalizePermission(permission));
        }
      });
      return { all, menu, other };
    },

    normalizeMenuPermission(permission) {
      return { id: permission.id, name: this.$options.filters.uppercaseFirst(permission.name.substring(10)), disabled: permission.disabled || false };
    },

    normalizePermission(permission) {
      const disabled = permission.disabled || permission.name === 'manage permission';
      return { id: permission.id, name: this.$options.filters.uppercaseFirst(permission.name), disabled: disabled };
    },

    confirmPermission() {
      const checkedOther = this.$refs.otherPermissions.getCheckedKeys();
      this.dialogPermissionLoading = true;

      userResource.updatePermission(this.currentUserId, { permissions: checkedOther }).then(response => {
        this.$message({
          message: 'Uprawnienia zostały dodane',
          type: 'success',
          duration: 5 * 1000,
        });
        this.dialogPermissionLoading = false;
        this.dialogPermissionVisible = false;
      });
    },
    
    // Avatar upload methods
    handleAvatarSuccess(res, file) {
      if (res.success) {
        this.newUser.avatarUrl = res.url;
        this.newUser.avatar = res.path;
        this.$message.success('Avatar został przesłany pomyślnie');
      } else {
        this.$message.error('Błąd podczas przesyłania avatara');
      }
    },
    beforeAvatarUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isJPG) {
        this.$message.error('Avatar musi być w formacie JPG lub PNG!');
        return false;
      }
      if (!isLt2M) {
        this.$message.error('Avatar nie może przekraczać 2MB!');
        return false;
      }

      // Call CSRF before upload
      return csrf().then(() => {
        return true;
      }).catch(err => {
        this.$message.error('Błąd uwierzytelniania');
        return false;
      });
    },
    removeAvatar() {
      this.newUser.avatarUrl = '';
      this.newUser.avatar = null;
    },
    
    // Video avatar upload methods
    handleVideoAvatarSuccess(res, file) {
      if (res.success) {
        this.newUser.videoAvatarUrl = res.url;
        this.newUser.video_avatar = res.path;
        this.$message.success('Video avatar został przesłany pomyślnie');
      } else {
        this.$message.error('Błąd podczas przesyłania video avatara');
      }
    },
    beforeVideoUpload(file) {
      const isVideo = file.type === 'video/mp4' || file.type === 'video/webm';
      const isLt10M = file.size / 1024 / 1024 < 10;

      if (!isVideo) {
        this.$message.error('Video avatar musi być w formacie MP4 lub WEBM!');
        return false;
      }
      if (!isLt10M) {
        this.$message.error('Video avatar nie może przekraczać 10MB!');
        return false;
      }

      // Call CSRF before upload
      return csrf().then(() => {
        return true;
      }).catch(err => {
        this.$message.error('Błąd uwierzytelniania');
        return false;
      });
    },
    removeVideoAvatar() {
      this.newUser.videoAvatarUrl = '';
      this.newUser.video_avatar = null;
    },
    
    // Create user method
    createUser() {
      this.$refs['userForm'].validate(async(valid) => {
        if (!valid) {
          this.$notify({
            title: 'Błąd',
            message: 'Popraw błędy w formularzu',
            type: 'error',
            duration: 2000,
          });
          return false;
        }
        
        this.userCreating = true;
        try {
          const userData = {
            name: this.newUser.name,
            email: this.newUser.email,
            password: this.newUser.password,
            roles: [this.newUser.accountType], // Backend expects roles as array, now using accountType
            phone: this.newUser.phone,
            city: this.newUser.city,
            street: this.newUser.street,
            zip_code: this.newUser.zip_code,
            country: this.newUser.country,
            nip: this.newUser.nip,
            avatar: this.newUser.avatar,
            video_avatar: this.newUser.video_avatar,
          };
          
          await userResource.store(userData);
          
          this.$message({
            type: 'success',
            message: 'Użytkownik został utworzony pomyślnie!',
            duration: 3000,
          });
          
          this.dialogFormVisible = false;
          this.getList(); // Refresh the list
        } catch (error) {
          console.error('Error creating user:', error);
          this.$message({
            type: 'error',
            message: 'Błąd podczas tworzenia użytkownika. Sprawdź czy email nie jest już zajęty.',
            duration: 5000,
          });
        } finally {
          this.userCreating = false;
        }
      });
    },
    
    // Password generation method
    generatePassword() {
      // Generate a password that meets all requirements
      const lowercase = 'abcdefghijklmnopqrstuvwxyz';
      const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const numbers = '0123456789';
      const specialChars = '!@#$%^&*(),.?":{}|<>';
      
      let password = '';
      
      // Ensure at least one character from each required category
      password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
      password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
      password += numbers.charAt(Math.floor(Math.random() * numbers.length));
      password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));
      
      // Fill the rest of the password (minimum 8 characters total)
      const allChars = lowercase + uppercase + numbers + specialChars;
      for (let i = password.length; i < 12; i++) {
        password += allChars.charAt(Math.floor(Math.random() * allChars.length));
      }
      
      // Shuffle the password to avoid predictable patterns
      password = password.split('').sort(() => 0.5 - Math.random()).join('');
      
      this.generatedPassword = password;
      this.newUser.password = password;
      this.newUser.confirmPassword = password;
      
      this.$message.success('Hasło zostało wygenerowane pomyślnie!');
    },
    
    // Password validation method
    validatePasswordStrength(rule, value, callback) {
      if (!value) {
        callback(new Error('Password is required'));
        return;
      }
      
      const hasMinLength = value.length > 7;
      const hasUppercase = /[A-Z]/.test(value);
      const hasLowercase = /[a-z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
      
      if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
        callback(new Error('Password must contain at least 8 characters including uppercase, lowercase, number and special character'));
      } else {
        callback();
      }
    },
    
    needsCompanyFix(user) {
      // Check if user has company role but no valid company_id
      const companyRoles = ['company', 'agency', 'developer'];
      const hasCompanyRole = user.roles && user.roles.some(role => companyRoles.includes(role));
      const hasValidCompanyId = user.company_id && user.company_id !== null && user.company_id !== 0 && user.company_id !== '';
      
      return hasCompanyRole && !hasValidCompanyId;
    },
    
    async handleFixCompany(userId, userName) {
      // Show dialog to select company type
      this.currentFixUserId = userId;
      this.currentFixUserName = userName;
      this.dialogFixCompanyVisible = true;
    },
    
    async confirmFixCompany() {
      try {
        this.fixCompanyLoading = true;
        
        // Call backend to fix the company with selected type
        const response = await userResource.fixCompany(this.currentFixUserId, this.selectedCompanyType);
        
        this.$message({
          type: 'success',
          message: `Firma "${this.getCompanyTypeLabel(this.selectedCompanyType)}" została utworzona pomyślnie!`,
          duration: 3000,
        });
        
        // Close dialog and refresh list
        this.dialogFixCompanyVisible = false;
        this.getList();
      } catch (error) {
        console.error('Error fixing company:', error);
        this.$message({
          type: 'error',
          message: 'Błąd podczas tworzenia firmy. Spróbuj ponownie.',
          duration: 5000,
        });
      } finally {
        this.fixCompanyLoading = false;
      }
    },
    
    getCompanyTypeLabel(type) {
      const typeMap = {
        'agency': 'Agencja nieruchomości',
        'developer': 'Deweloper'
      };
      return typeMap[type] || type;
    },
    
    getCSRFToken() {
      // Get XSRF token from cookie
      const token = document.cookie.split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='));
      return token ? decodeURIComponent(token.split('=')[1]) : '';
    }
  },
  computed: {
    uploadHeaders() {
      return {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': this.getCSRFToken()
      };
    },
    hasMinLength() {
      return this.newUser.password.length > 7;
    },
    hasUppercase() {
      return /[A-Z]/.test(this.newUser.password);
    },
    hasLowercase() {
      return /[a-z]/.test(this.newUser.password);
    },
    hasNumber() {
      return /\d/.test(this.newUser.password);
    },
    hasSpecialChar() {
      return /[!@#$%^&*(),.?":{}|<>]/.test(this.newUser.password);
    }
  },
};
</script>

<style lang="scss" scoped>
.bulk-notify-hint {
  font-size: 12px;
  line-height: 1.4;
  color: #909399;
}
.edit-input {
  padding-right: 100px;
}
.cancel-btn {
  position: absolute;
  right: 15px;
  top: 10px;
}
.dialog-footer {
  text-align: left;
  padding-top: 0;
  margin-left: 150px;
}
.app-container {
  flex: 1;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
  .block {
    float: left;
    min-width: 250px;
  }
  .clear-left {
    clear: left;
  }
}

/* Password generation styles */
.password-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
  
  .generate-btn {
    flex-shrink: 0;
  }
}

.generated-password-display {
  margin-top: 8px;
  padding: 8px;
  background-color: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 4px;
  font-size: 12px;
  
  .password-note {
    color: #dc2626;
    font-weight: 500;
    margin-top: 4px;
  }
}

.password-strength {
  margin-top: 12px;
  padding: 8px;
  background-color: #f9fafb;
  border-radius: 4px;
  
  .strength-title {
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 4px;
    color: #374151;
  }
  
  .strength-indicators {
    display: flex;
    flex-direction: column;
    gap: 2px;
    
    p {
      font-size: 11px;
      margin: 0;
    }
  }
}

/* User info cell styles */
.user-info-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  .user-avatar-mini {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #e4e7ed;
  }
  
  .user-details {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    
    .user-name {
      font-weight: 500;
    }
    
    .video-indicator {
      font-size: 12px;
      color: #67c23a;
      
      i {
        margin-right: 2px;
      }
    }
  }
}

/* Company warning styles */
.company-warning {
  margin-top: 4px;
  
  .el-tag {
    font-size: 10px;
    padding: 2px 4px;
    
    i {
      margin-right: 2px;
      font-size: 10px;
    }
  }
}

/* Avatar upload styles */
.avatar-upload-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  .avatar-uploader {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: border-color 0.3s;
    
    &:hover {
      border-color: #409eff;
    }
    
    .avatar-preview {
      width: 120px;
      height: 120px;
      display: block;
      object-fit: cover;
    }
    
    .avatar-uploader-icon {
      font-size: 28px;
      color: #8c939d;
      width: 120px;
      height: 120px;
      line-height: 120px;
      text-align: center;
      display: block;
    }
  }
  
  .upload-tips {
    margin: 8px 0;
    font-size: 12px;
    color: #666;
    
    p {
      margin: 2px 0;
    }
  }
}

/* Video avatar upload styles */
.video-avatar-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  .video-uploader {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: border-color 0.3s;
    
    &:hover {
      border-color: #409eff;
    }
    
    .video-preview {
      width: 200px;
      height: 120px;
      display: block;
      object-fit: cover;
    }
    
    .video-uploader-icon {
      font-size: 28px;
      color: #8c939d;
      width: 200px;
      height: 120px;
      line-height: 120px;
      text-align: center;
      display: block;
    }
  }
  
  .upload-tips {
    margin: 8px 0;
    font-size: 12px;
    color: #666;
    
    p {
      margin: 2px 0;
    }
  }
}

/* Fix Company Dialog styles */
.company-type-info {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  
  p {
    margin: 4px 0;
    font-size: 14px;
  }
  
  .info-note {
    font-size: 12px;
    color: #6c757d;
    font-style: italic;
    margin-top: 8px;
  }
}
</style>
